/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { CheckBoxConfig } from '../../../../../../shared/ui/src/lib/models/check-box-config';
import { GlobalConstants } from '../../constants/global-constants';
import { Observable, Subject, Subscription, filter, from, of, switchMap, take, takeUntil } from 'rxjs';
import { SysquePopupConfig } from '../../models/sysque-popup-config';
import { createDialog } from '../../popup-dialog-factory/popup-dialog.facade';
import { TabDescription } from 'libs/sysque/toolbelt/data-access/src/lib/domain/tab-description';
import { Item } from 'libs/sysque/toolbelt/data-access/src/lib/domain/item';
import { TooltipItem } from '../../../../../../../sysque/toolbelt/data-access/src/lib/domain/tooltip-item';
import {
  ISystemPanelFrameFacade,
  SYSTEM_PANEL_FRAME_FACADE_TOKEN,
} from '../../../../../data-access/src/lib/domain/facades-models/system-panel-frame-facade-interface';
import { SysqueElectricalState } from '../../models/sysque-electrical-state';
import { SystemInfo } from 'libs/sysque/toolbelt/data-access/src/lib/domain/system-info';
import { DownloadFamiliesReturnCodes } from '../../models/download-families-return-codes';
import { RootPathSelectorComponent } from '../root-path-selector/root-path-selector.component';
import { PanelDetails } from '../../models/panel-detail';
import { BooleanObject } from '../../models/boolean-object';
import { SysqueNumberBoxVM } from 'libs/sysque/shared/ui/src/lib/models/sysque-number-box-vm';
import { SelectedPanelDataItem } from '../../models/selected-panel-data-item';
import { CircuitList } from '../../models/paneldata-info';
import { PanelCircuitDataReturnCodes } from '../../models/panel-circuit-data-return-codes';
import { DxContextMenuComponent } from 'devextreme-angular/ui/context-menu';
import { PastedItemsInfo } from '../../models/pasted-items-info';
import { SystemCategories } from '../../models/system-categories';
import { CategoryScopeComponent } from '../category-scope/category-scope.component';
import { SysQueCopyPasteService } from '../../../../../utils/src/lib/sysque-copy-paste/sysque-copy-paste.service';
import { AcrylicSurfaceService } from '../../acrylic-surface/acrylic-surface.service';
import { ProcessingService } from '../../../../../utils/src/lib/sysque-processing/sysque-processing.service';
import { IFamilyInformationItem } from '../add-family-information/add-family-information-types';
import { SystemOperationsService } from '../system-operations/system-operations.service';

@Component({
  selector: 'sysque-system-panel-frame',
  templateUrl: './system-panel-frame.component.html',
  styleUrls: ['./system-panel-frame.component.scss'],
  standalone: false
})
export class SystemPanelFrameComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() addFittingMode: boolean;
  @Input() categoryList$: Observable<TabDescription[]>;
  @Input() showAddFittingBtn: boolean;
  @Input() manageMode: boolean;
  @Input() showRootPath: boolean;
  public isPanelSystem = false;
  public tooltipItems: TooltipItem[] = [];
  public selectedCategory: string;
  public dockedState = new SysqueElectricalState();
  public projectList$: Observable<string[]>;
  public panelList$: Observable<PanelDetails[]>;
  public circuitList$: Observable<string[]>;
  public panelType$: Observable<boolean>;
  public panelsList: PanelDetails[] = [];
  public circuitList: string[] = [];
  public isHangerNeed: boolean = false;
  public isClearanceNeed: boolean = false;
  public isClearanceNeed$: Observable<BooleanObject>;
  public isHangerNeed$: Observable<BooleanObject>;
  public dockVars$: Observable<boolean[]>;
  public isClearanceCategory: boolean = false;
  //as sample data
  public checkBoxConfigs: CheckBoxConfig[] = [];
  public checkedCircuit: CheckBoxConfig[] = [];
  public numberInput: SysqueNumberBoxVM[] = [];
  public systemInfo$: Observable<SystemInfo[]> = of([]);
  public stateSystemInfo$: Observable<SystemInfo[]> = of([]);
  public isLoading = false;
  public areMissing = false;
  public isError = false;
  public dialogTitle = '';
  public dialogBody = '';
  public buttonOkOrYes = '';
  public buttonNo = GlobalConstants.No;
  public isButtonNoDisabled = true;
  public progressBarTitle: string;
  public progressBarBody: string;
  public disableApplyBtn = true;
  public selectedItem: SelectedPanelDataItem;
  public selectedCircuits: CircuitList[] = [];
  public showApplyBtn = false;
  public ProjectList$: Observable<string[]>;
  public projectSubscription: Subscription;
  public WireSpecList$: Observable<string[]>;
  public wireSpecSubscription: Subscription;
  public projectList: string[] = [];
  public wireSpecList: string[] = [];
  public hangerNeededLabel = GlobalConstants.HangerNeeded;
  public applyToSelectionButton = GlobalConstants.ApplyToSelection;
  public selectedSystemIndex: number;

  //#region Copy/Paste members

  @Output() pasteError: EventEmitter<string> = new EventEmitter<string>();
  @Output() showDuplicateError: EventEmitter<PastedItemsInfo[]> = new EventEmitter<PastedItemsInfo[]>();
  @ViewChild(CategoryScopeComponent) categoryScope: CategoryScopeComponent;
  @ViewChildren(DxContextMenuComponent) contextMenus: QueryList<DxContextMenuComponent>;
  public rightClickMenuItems = [{ text: GlobalConstants.Paste }];
  public showRightClickMenu = false;
  public selectedItems: Item[] = [];
  public categories = SystemCategories.getSystemCategories().slice(2, 7);
  public warningMsg: string = '';
  public copiedItemInfo: PastedItemsInfo = new PastedItemsInfo();
  public copiedItemList: PastedItemsInfo[] = [];
  public duplicateItemList: PastedItemsInfo[];
  public srcSelectedSystem: string;
  public srcSelectedCategory: string;
  public destSelectedSystem: string;
  public destSelectedCategory: string;
  public isErrorMessage = true;
  public showHangerAlert = false;
  public popUpMsgContent: string;
  private isHangerFromCopyPaste = false;
  private previousSystem: string = '';
  private readonly closed$ = new Subject<void>();
  private resetNumberBoxEvent = new EventEmitter<void>();

  //#endregion

  constructor(
    @Inject(SYSTEM_PANEL_FRAME_FACADE_TOKEN) public facade: ISystemPanelFrameFacade,
    public copyPasteService: SysQueCopyPasteService,
    public proccessingService: ProcessingService,
    private cd: ChangeDetectorRef,
    private acrylicSurfaceService: AcrylicSurfaceService,
    private systemOperationsService: SystemOperationsService
  ) {}

  public ngOnInit(): void {
    window.addEventListener(GlobalConstants.STORAGE, this.storageEventListener.bind(this));
    this.facade.getConfigurationLocation();
    this.previousSystem = '';
    this.showApplyBtn = false;
    this.isPanelSystem = false;
    this.dockedState = this.facade.getLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY);
    if (!this.dockedState) {
      this.dockedState = new SysqueElectricalState();
      this.facade.updateLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY, this.dockedState);
    }
    this.dockedState.selectedSystem = '';
    this.facade.updateLocalStorage(GlobalConstants.DockableKey, true);

    this.proccessingService.systemsInfo$
      .pipe(
        takeUntil(this.closed$),
        switchMap((value) => {
          if (value.length === 0) {
            return this.facade.getSystems().pipe(take(1));
          } else {
            return of(value);
          }
        })
      )
      .subscribe((systems) => {
        this.setProperties(systems);
      });
  }

  public onErrorClosed(): void {
    this.isErrorMessage = false;
  }

  private setProperties(value: SystemInfo[]) {
    this.systemInfo$ = of(value);
    this.stateSystemInfo$ = of(value);
    this.dockedState.systemList = value;

    this.cd.detectChanges();
    this.selectedItems = [];
    this.updateCopyPasteValues();
    this.facade.updateLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY, this.dockedState);
  }

  public ngOnDestroy(): void {
    window.removeEventListener(GlobalConstants.STORAGE, this.storageEventListener.bind(this));
    this.closed$.next();
    this.closed$.complete();
  }

  @ViewChild(RootPathSelectorComponent) roothPath: RootPathSelectorComponent;
  public async ngAfterViewInit(): Promise<void> {
    if (this.roothPath) {
      this.roothPath.rootPathChanged.pipe(takeUntil(this.closed$)).subscribe(async (value) => {
        if (value === true) {
          this.checkIfUnreferencedFamilies();
          await this.updateFittingsList();
          this.cd.detectChanges();
        }
      });
    }
  }

  public async onValueChanged(e: any): Promise<void> {
    if (e.value === undefined || e.value === '' || !this.facade.checkIfCurrentWindowIsDockable()) {
      return;
    }

    this.clearFittingsAndCategories();

    if (e.value) {
      await this.initializeSystem(e.value);
    }
    this.dockedState.selectedSystem = e.value;
    this.showApplyBtn = true;
    this.facade.updateLocalStorage(GlobalConstants.SELECTED_SYSTEM_NAME, this.dockedState.selectedSystem);

    await this.retrieveCategories(e.value as string);
    this.checkIfUnreferencedFamilies();

    this.dockedState = this.facade.getLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY);

    await this.updateFittingsList();

    this.acrylicSurfaceService.hide();
    this.selectedSystemIndex = this.dockedState.systemList.findIndex((x) => x.name === this.dockedState.selectedSystem);
  }

  public async onHangerValueChanged(event: any): Promise<void> {
    this.dockedState = this.facade.getLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY);
    this.dockedState.isHangerNeeded = event.detail;
    await this.facade.updateHangerNeeded(this.dockedState.selectedSystem, event.detail as boolean);
    this.dockedState.systemList.forEach((system) => {
      if (system.name === this.dockedState.selectedSystem) {
        system.isHangerEnabled = event.detail;
        return;
      }
    });
    this.facade.updateLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY, this.dockedState);
  }

  public updateIsClearance(event: boolean): void {
    this.dockedState = this.facade.getLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY);
    this.dockedState.isCableTrayClearanceNeeded = event;
    this.facade.updateClearance(this.dockedState.selectedSystem, event);
    this.isClearanceNeed$ = this.facade.selectClearance();
    this.isClearanceNeed$.pipe(takeUntil(this.closed$)).subscribe((data) => {
      this.isClearanceNeed = data.value;
    });
    this.dockedState.systemList.find((x) => x.name === this.dockedState.selectedSystem).isClearance = event;
    this.facade.updateLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY, this.dockedState);

    // After update the clearance checkbox, check if unreferenced families are missing
    this.checkIfUnreferencedFamilies();

    // Refresh the fittings list
    this.updateFittingsList();
  }

  public async onProjectValueChanged(event: any): Promise<void> {
    this.dockedState = this.facade.getLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY);
    this.ClearValueOnProjectChange();
    this.dockedState.projectName = event;
    this.facade.updateLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY, this.dockedState);
    this.wireSpecList = await this.facade.getWireFillList(event, this.dockedState.selectedCategory);
  }

  private ClearValueOnProjectChange(): void {
    this.circuitList$ = of([]);
    this.circuitList = [];
    this.WireSpecList$ = of([]);
    this.wireSpecList = [];
    this.dockedState.wirefillSpec = '';
  }

  public onWireFillValueChanged(event: any): void {
    this.dockedState = this.facade.getLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY);
    this.dockedState.wirefillSpec = event;
    this.facade.updateLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY, this.dockedState);
  }

  public checkIfUnreferencedFamilies(): void {
    from(this.facade.checkIfUnreferencedFamilies(this.dockedState.selectedSystem))
      .pipe(take(1))
      .subscribe((result) => {
        switch (result) {
          case DownloadFamiliesReturnCodes.Download:
            this.areMissing = true;
            this.isError = false;
            this.buttonOkOrYes = GlobalConstants.Yes;
            this.isButtonNoDisabled = false;
            this.dialogTitle = GlobalConstants.DownloadUnreferencedFamiliesTitle;
            this.dialogBody = GlobalConstants.DownloadUnreferencedFamiliesBody;
            break;
          case DownloadFamiliesReturnCodes.NotDownload:
            this.areMissing = false;
            this.isError = false;
            break;
          case DownloadFamiliesReturnCodes.Error:
            this.areMissing = false;
            this.isError = true;
            this.dialogTitle = GlobalConstants.TitleForUnableToDownloadUnreferencedamilies;
            this.dialogBody = GlobalConstants.BodyForUnableToDownloadUnreferencedFamilies;
            this.buttonOkOrYes = GlobalConstants.Dismiss;
            this.isButtonNoDisabled = true;
            break;
        }
      });
  }

  public openHangerSpecification(item: IFamilyInformationItem): void {
    item.specId = '';
    item.descriptionInstance = '';
    item.tooltipVisible = false;

    item.systemName = this.dockedState.selectedSystem;
    this.facade.updateLocalStorage(GlobalConstants.FAMILY_INFORMATION_ITEM, item);

    this.acrylicSurfaceService.show();
    this.facade.openHangerSpecification();
    this.showHangerAlert = false;
  }

  public async onYesClick(): Promise<void> {
    if (this.showHangerAlert) {
      this.facade.updateLocalStorage(GlobalConstants.HangerCopySetUp, true);
      const result = await this.facade.getFittings(this.dockedState.selectedSystem, 'BuildingLayout');
      let hangerItemList: IFamilyInformationItem[] = result.filter((item) => item.isHanger) as IFamilyInformationItem[];

      // Add isFromDuplicate property to each item
      hangerItemList.forEach((item) => {
        item.isFromDuplicate = false;
        item.familyCategoryName = 'Pipe Accessories';
        item.parameters = undefined;
        item.pushParameters = '';
      });

      // Open hanger specification
      if (hangerItemList.length > 0) {
        const index = this.isHangerFromCopyPaste ? hangerItemList.length - 1 : 0;
        this.openHangerSpecification(hangerItemList[index]);
      }

      // Remove the first item from the list after processing
      hangerItemList = hangerItemList.slice(1);
      return;
    }
    this.areMissing = false;
    if (this.buttonOkOrYes === GlobalConstants.Yes) {
      this.isLoading = true;
      this.progressBarTitle = GlobalConstants.Downloading;
      this.progressBarBody = GlobalConstants.UnreferencedFamilies + this.dockedState.selectedSystem;
      this.cd.detectChanges();

      setTimeout(() => {
        this.facade
          .downloadUnreferencedFamilies(this.dockedState.selectedSystem)
          .pipe(takeUntil(this.closed$))
          .subscribe((response) => {
            this.isError = !response;
            if (this.isError) {
              this.dialogTitle = GlobalConstants.TitleForUnableToDownloadUnreferencedamilies;
              this.dialogBody = GlobalConstants.BodyForUnableToDownloadUnreferencedFamilies;
              this.buttonOkOrYes = GlobalConstants.Dismiss;
              this.isButtonNoDisabled = true;
            } else {
              // Update the fittings list
              this.updateFittingsList();
            }
            this.isLoading = false;
          });
      }, 1000);
    } else {
      this.isError = false;
    }
  }

  public onNoClick(): void {
    if (this.showHangerAlert) {
      this.showHangerAlert = false;
      return;
    }
    this.areMissing = false;
    this.isLoading = false;
  }

  public onTextInputChanged(event: any): void {
    this.dockedState = this.facade.getLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY);
    if (this.numberInput.some((item) => item.label === event.label)) {
      this.numberInput = this.numberInput.filter((item) => item.label !== event.label);
      this.dockedState.cableTrayNumberText = [];
      this.numberInput.push(event);
    } else {
      this.numberInput.push(event);
    }
    this.dockedState.cableTrayNumberText = this.numberInput;
    this.facade.updateLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY, this.dockedState);
  }

  public clearFittingsAndCategories(): void {
    this.clearCommonValues();
    this.facade.updateLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY, this.dockedState);
  }

  public updateCopyPasteValues(): void {
    this.copiedItemList = [];
    this.duplicateItemList = [];
    this.selectedItems = [];
    this.copyPasteService.setCopiedItemsName([]);
    this.copyPasteService.setCopiedItemsList([]);
    this.copyPasteService.setDuplicateItemsList([]);
    this.copyPasteService.setUniqueCopiedItemsList([]);
  }

  private clearCommonValues(): void {
    this.dockedState.fittingList = [];  
    this.categoryList$ = of([]);
    this.circuitList$ = of([]);
    this.checkedCircuit = [];
    this.panelList$ = of([]);
    this.projectList$ = of([]);
    this.projectList = [];
    this.wireSpecList = [];
    this.dockedState.projectName = '';
    this.dockedState.wirefillSpec = '';
    this.dockedState.cableTrayNumberText = [];
    this.dockedState.projectAndPanelName = [];
    this.dockedState.checkBoxConfigs = [];
    this.checkBoxConfigs = [];
    this.panelsList = [];
    this.disableApplyBtn = true;
    this.resetNumberBoxEvent.emit();
    this.cd.markForCheck();
  }

  private async initializeSystem(selectedSystem: any): Promise<void> {
    this.showAddFittingBtn = true;
    this.dockedState.selectedSystem = selectedSystem;
    this.facade.updateLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY, this.dockedState);
    await this.initializeSystemTypeInfo(selectedSystem);
  }

  private async initializeSystemTypeInfo(selectedSystem: any): Promise<void> {
    this.getIsHangerAndClearanceValue(selectedSystem);
    this.isPanelSystem = await this.facade.getPanelType(this.dockedState.selectedSystem);
  }

  private getIsHangerAndClearanceValue(selectedSystem: any): void {
    this.dockedState.isHangerNeeded = this.dockedState.systemList.find(
      (x) => x.name === selectedSystem
    ).isHangerEnabled;
    this.dockedState.isCableTrayClearanceNeeded = this.dockedState.systemList.find(
      (x) => x.name === selectedSystem
    ).isClearance;
  }

  private async retrieveCategories(selectedSystem: string): Promise<void> {
    const categoryList = await this.facade.getCategories(selectedSystem);
    if (categoryList.length > 0) {
      this.dockedState.categoryList = categoryList;
      this.dockedState.selectedCategory = this.dockedState.categoryList[0].tabName;
      this.selectedCategory = this.dockedState.categoryList[0].tabName;
      this.facade.updateLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY, this.dockedState);

      this.isClearanceCategory = !(
        this.selectedCategory === GlobalConstants.Conduit || this.selectedCategory === GlobalConstants.FieldFabConduit
      );
      if (this.isClearanceCategory) {
        this.facade.updateLocalStorage(GlobalConstants.UPDATE_PROCESS_FITTINGS_ONLY, 'true');
      } else {
        this.facade.updateLocalStorage(GlobalConstants.UPDATE_PROCESS_FITTINGS_ONLY, 
          this.facade.getLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY)?.onlyFittingProcess);
      }
  
      this.toggleDirectTakeoffLocalStorage();
    } 
  }

  public getUpdatedCircuit(event: any): void {
    this.dockedState = this.facade.getLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY);
    if (event.checked === false) {
      this.checkedCircuit = this.checkedCircuit.filter((item) => item.label !== event.label);
      if (this.checkedCircuit.length === 0) {
        this.disableApplyBtn = true;
      }
    } else {
      this.checkedCircuit.push(event);
      const selectionExists: boolean = this.facade.getLocalStorage(GlobalConstants.ELEMENT_SELECTION_STATUS);
      this.disableApplyBtn = !selectionExists;
    }
    this.dockedState.checkBoxConfigs = this.checkedCircuit;
    this.facade.updateLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY, this.dockedState);
    this.cd.detectChanges();
  }

  public async onPanelValueChanged(event: any): Promise<void> {
    this.dockedState = this.facade.getLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY);
    this.checkBoxConfigs = [];
    this.checkedCircuit = [];
    this.circuitList$ = of([]);
    this.disableApplyBtn = true;
    this.circuitList = await this.facade.getCircuitList(event.PanelName, event.ProjectName);

    this.circuitList.forEach((element, index) => {
      this.checkBoxConfigs.push(CheckBoxConfig.create(element, false, index));
    });

    this.dockedState.projectAndPanelName = [event.ProjectName, event.PanelName];
    this.dockedState.checkBoxConfigs = [];
    this.dockedState.panelName = event.PanelName;
    this.dockedState.projectName = event.ProjectName;
    this.facade.updateLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY, this.dockedState);
  }

  public onPanelFocusIn(): void {
    if (this.panelsList?.length) {
      return;
    }
    this.updatePanelList();
  }

  public updatePanelList(): void {
    this.panelList$ = from(this.facade.notifyGetDockablePanelListAction());
    this.panelList$.pipe(take(1)).subscribe((data) => {
      this.panelsList = data;
      this.facade.updateLocalStorage(GlobalConstants.PANEL_LIST, this.panelsList);
    });
  }

  public onProjectFocusIn(): void {
    if (this.projectList?.length) {
      return;
    }
    this.updateProjectList();
  }

  public updateProjectList(): void {
    this.facade.getProjectList();
    this.ProjectList$ = this.facade.selectProjectList();
    this.projectSubscription = this.ProjectList$.pipe(
      filter((categories) => Object.keys(categories).length > 0),
      takeUntil(this.closed$)
    ).subscribe((response) => {
      this.projectList = response;
      this.facade.updateLocalStorage(GlobalConstants.PROJECT_LIST, this.projectList);
    });
  }

  public onCategoryChanged(e: any): void {
    if (e.value === undefined) {
      return;
    }

    if (!this.dockedState.selectedSystem ||
      (this.dockedState.selectedSystem === this.previousSystem && this.dockedState.selectedCategory === e.value)) {
      return;
    }

    this.previousSystem = this.dockedState.selectedSystem;
    this.dockedState = this.facade.getLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY);
    this.selectedCategory = e.value;
    this.dockedState.selectedCategory = this.selectedCategory;
    this.dockedState.fittingList = [];
    this.facade.updateLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY, this.dockedState);

    this.updateFittingsList();
  }

  // to be removed
  public handleContextMenuItem(item: string): void {
    this.facade.updateLocalStorage(GlobalConstants.SCOPE, GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY);
    this.facade.updateLocalStorage(GlobalConstants.ACRYLIC_SURFACE, true);
    this.acrylicSurfaceService.show();
    let sysquePopupConfig = new SysquePopupConfig();
    switch (item) {
      case GlobalConstants.CreateSystem:
        createDialog(sysquePopupConfig, GlobalConstants.CREATE_SYSTEM_NAME_DIALOG);
        this.facade.sendPopupRoutingAction(
          GlobalConstants.CREATE_SYSTEM_NAME_DIALOG,
          sysquePopupConfig.width,
          sysquePopupConfig.height
        );
        break;

      case GlobalConstants.RenameSystem:
        createDialog(sysquePopupConfig, GlobalConstants.RENAME_SYSTEM_NAME_DIALOG);
        this.facade.sendPopupRoutingAction(
          GlobalConstants.RENAME_SYSTEM_NAME_DIALOG,
          sysquePopupConfig.width,
          sysquePopupConfig.height
        );
        break;

      case GlobalConstants.DeleteSystem:
        createDialog(sysquePopupConfig, GlobalConstants.DELETE_SYSTEM_NAME_DIALOG);
        this.facade.sendPopupRoutingAction(
          GlobalConstants.DELETE_SYSTEM_NAME_DIALOG,
          sysquePopupConfig.width,
          sysquePopupConfig.height
        );
        break;

      default:
        break;
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeunloadHandler() {
    this.showAddFittingBtn = false;
  }

  public async storageEventListener(event: StorageEvent): Promise<void> {
    if (!event) return;
    switch (event.key) {
      case GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY:
        this.dockedState = event.newValue && JSON.parse(event.newValue);
        this.categoryList$ = of(this.dockedState.categoryList);
        if (!this.facade.checkIfCurrentWindowIsDockable()) {
          this.cd.detectChanges();
        }
        break;
      case GlobalConstants.CONFIGURATION_LOCATION:
        this.showApplyBtn = false;
        this.showAddFittingBtn = false;
        this.cd.detectChanges();
        localStorage.removeItem(GlobalConstants.CONFIGURATION_LOCATION);
        break;
      case GlobalConstants.SYSTEM_CREATION_SUCCESS:
        if (event.newValue === GlobalConstants.TRUE.toLocaleLowerCase() &&
          localStorage.getItem(GlobalConstants.DockableKey)) {
          this.systemOperationsService.createSystem(this.stateSystemInfo$, this.dockedState);
          this.selectedSystemIndex = this.dockedState.systemList.findIndex((x) => x.name === this.dockedState.selectedSystem);
          this.cd.markForCheck();
        }
        break;
      case GlobalConstants.SYSTEM_DELETED_SUCCESS:
        if (event.newValue === GlobalConstants.TRUE.toLocaleLowerCase() &&
          localStorage.getItem(GlobalConstants.DockableKey)) {
          this.systemOperationsService.deleteSystem(this.stateSystemInfo$, this.dockedState, (this.selectedSystemIndex - 1));
          this.cd.markForCheck();
        }
        break;
      case GlobalConstants.SYSTEM_RENAMED_SUCCESS:
        if (event.newValue === GlobalConstants.TRUE.toLocaleLowerCase() &&
          localStorage.getItem(GlobalConstants.DockableKey)) {
          this.systemOperationsService.renameSystem(this.stateSystemInfo$, this.dockedState, this.selectedSystemIndex);
          this.cd.markForCheck();
        }
        break;
      case GlobalConstants.SYSTEM_DUPLICATED_SUCCESS:
      case GlobalConstants.IMPORT_SYSTEMS_SUCCESS:
        if (event.newValue === GlobalConstants.TRUE.toLocaleLowerCase()) {
          this.storageEventDuplicateImportSystem();
        }
        break;
      case GlobalConstants.SYSTEM_MANAGER_OPERATIONS_SUCCESS:
        if (event.newValue === GlobalConstants.TRUE.toLocaleLowerCase()) {
          this.storageEventSystemManagerOperations();
        }
        break;
      case GlobalConstants.ELEMENT_SELECTION_STATUS:
        this.enableApplyBtn(JSON.parse(event.newValue));
        break;
      case GlobalConstants.DOCKABLE_PANEL_DATA_RESPONSE:
        this.showAlertWindow(event);
        break;
      case GlobalConstants.ADD_FITTING_MODE_CLICKED:
        this.facade.updateLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY, this.dockedState);
        break;
      case GlobalConstants.CanExecutePasteLogic:
        if (event.newValue === 'true') {
          this.copyPasteService.duplicateItemList$.pipe(takeUntil(this.closed$)).subscribe((data) => {
            this.copiedItemList = data;
          });
          this.onPasteEventLogic(this.copiedItemList);
        }
        break;
      case GlobalConstants.REORDERED_FITTINGS_SUCCESS:
      case GlobalConstants.ELECTRICAL_SETTINGS_SAVE_SUCCESS:
        if (JSON.parse(event.newValue) === GlobalConstants.TRUE.toLocaleLowerCase()) {
          // Update the fittings list
          this.updateFittingsList();
        }
        break;
      case GlobalConstants.ACRYLIC_OVERLAY:
        this.acrylicSurfaceService.syncState(event);
        break;
      case GlobalConstants.DOWNLOAD_MISSING_FAMILIES_SUCCESS:
      case GlobalConstants.LINE_GRAPHICS_SUCCESS:
        // Update the fittings list
        this.updateFittingsList();
        break;
      case GlobalConstants.SYSTEM_NAME_GO_TO_SYSTEM:
        this.dockedState.selectedSystem = JSON.parse(event.newValue);
        if (this.dockedState.selectedSystem === GlobalConstants.RESET_DOCK_TO_INITIAL_STATE) {
          // If the selected system is empty, reset the dockable to the initial state          
          this.resetDockableToTheInitialState();
          return;
        }
        this.facade.updateLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY, this.dockedState);
        this.cd.detectChanges();
        break;
      case GlobalConstants.UPDATE_PROJECT_LIST:
        if (event.newValue === GlobalConstants.TRUE.toLocaleLowerCase()) {
          this.updateProjectList();
          this.facade.updateLocalStorage(GlobalConstants.UPDATE_PROJECT_LIST, false);
        }
        break;
      case GlobalConstants.UPDATE_PANEL_LIST:
        if (event.newValue === GlobalConstants.TRUE.toLocaleLowerCase()) {
          this.updatePanelList();
          this.facade.updateLocalStorage(GlobalConstants.UPDATE_PANEL_LIST, false);
        }
        break;
      case GlobalConstants.SYSQUE_CONNECT_SYSTEM_NAME:
        if (event.newValue !== null && JSON.parse(event.newValue) !== '' && event.newValue !== undefined) {
          this.dockedState.selectedSystem = JSON.parse(event.newValue);
          this.facade.updateLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY, this.dockedState);
          this.cd.detectChanges();
        }
        break;
      case GlobalConstants.RELOAD_FITTINGS:
        if (event.newValue !== null && JSON.parse(event.newValue) === true && event.newValue !== undefined) {
          this.updateFittingsList();
          break;
        }
      case GlobalConstants.SET_UP_HANGER:
        this.showHangerAlert = true;
        this.dialogTitle = GlobalConstants.HangerSetUpAlertTitle;
        this.dialogBody = GlobalConstants.HangerSetUpAlertBody;
        this.buttonOkOrYes = GlobalConstants.YES;
        this.buttonNo = GlobalConstants.NO;
        this.isButtonNoDisabled = false;
        this.cd.detectChanges();
        break;
      case GlobalConstants.UPDATE_PANEL_TYPE:
        this.isPanelSystem = JSON.parse(event.newValue);
        this.cd.detectChanges();
        break;
      case GlobalConstants.UpdateSystemInfoOnDockableFromAddFittings:
        this.isClearanceCategory = !(
          this.selectedCategory === GlobalConstants.Conduit || this.selectedCategory === GlobalConstants.FieldFabConduit
        );
        if(!this.isClearanceCategory){
          this.dockedState.isCableTrayClearanceNeeded = false;          
        }
        await this.initializeSystemTypeInfo(this.dockedState.selectedSystem);
        this.cd.markForCheck();
        localStorage.removeItem(GlobalConstants.UpdateSystemInfoOnDockableFromAddFittings);        
        break;
    }
  }

  private resetDockableToTheInitialState(): void {
    this.clearCommonValues();
    this.showAddFittingBtn = false;
    this.showApplyBtn = false;
    this.dockedState.selectedCategory = '';
    this.dockedState.selectedSystem = '';
    this.dockedState.categoryList = [];
    this.acrylicSurfaceService.hide();
    this.facade.updateLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY, this.dockedState);
  }

  //#region Systems operations

  private toggleDirectTakeoffLocalStorage(): void {
    if (this.selectedCategory == GlobalConstants.FieldFabConduit) {
      this.facade.updateLocalStorage(GlobalConstants.IS_DIRECT_TAKEOFF_ENABLED, false);
    } else {
      this.facade.updateLocalStorage(GlobalConstants.IS_DIRECT_TAKEOFF_ENABLED, true);
    }
  }

  /**
   * Handle the events for Duplicate System and Import System(s)
   */
  private storageEventDuplicateImportSystem(): void {
    const systemSelected = this.dockedState.selectedSystem;

    this.updateSystemsList(false);

    this.dockedState.selectedSystem = systemSelected;
  }

  /**
   * Handle the events for Duplicate System, Import Systems and Reordered Systems
   */
  private storageEventSystemManagerOperations(): void {
    const systemSelected = this.dockedState.selectedSystem;

    this.updateSystemsList(true, systemSelected);
  }

  /**
   * This method updates the systems list in the state
   * @param isVisibilityChanged - if the visibility of the system was changed
   * @param systemSelected - the system selected
   */
  private updateSystemsList(isVisibilityChanged: boolean, selectedSystem?: string): void {
    this.facade
      .getSystems()
      .pipe(take(1))
      .subscribe((systems) => {
        this.setProperties(systems);

        // If the changes includes the system visibility, then refresh the values
        if (isVisibilityChanged) {
          this.checkSystemVisibility(selectedSystem);
        }

        this.cd.detectChanges();
      });
  }

  /**
   * This method checks the visibility of the system selected
   * @param systemSelected - the system selected
   */
  private checkSystemVisibility(systemSelected: string): void {
    const system: SystemInfo = this.dockedState.systemList.find((x) => x.name === systemSelected);

    // If the system is not visible, then clear the fittings and categories
    // Otherwise, update the fittings and categories
    if (system) {
      this.onValueChanged({ value: systemSelected, event: 'System Manager operations' });
    } else {
      this.selectedCategory = '';
      this.dockedState = this.facade.getLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY);
      this.dockedState.selectedSystem = '';
      this.dockedState.categoryList = [];
      this.categoryList$ = of([]);
      this.clearFittingsAndCategories();
      this.facade.updateLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY, this.dockedState);
    }
  }

  //#endregion

  public showAlertWindow(event: StorageEvent): void {
    const response = JSON.parse(event.newValue);
    let componentAlertTitle = '';
    let msgContent = '';
    switch (response) {
      case PanelCircuitDataReturnCodes.InvalidSelection:
        componentAlertTitle = GlobalConstants.SelectDockableApplyPanelErrorTitle;
        msgContent = GlobalConstants.SelectDockableApplyPanelErrorBody;
        break;
      case PanelCircuitDataReturnCodes.Success:
        componentAlertTitle = GlobalConstants.SelectDockableApplyPanelApplied;
        msgContent = GlobalConstants.SelectDockableApplyPanelAppliedBody;
        break;
      case PanelCircuitDataReturnCodes.CableTrayMismatch:
        componentAlertTitle = GlobalConstants.SelectApplyPanelSelectionMismatch;
        msgContent = GlobalConstants.SelectDockableDataSelectConduitBody;
        break;
      case PanelCircuitDataReturnCodes.ConduitMismatch:
        componentAlertTitle = GlobalConstants.SelectApplyPanelSelectionMismatch;
        msgContent = GlobalConstants.SelectDockableDataSelectCableBody;
        break;
      case PanelCircuitDataReturnCodes.InvalidWireFillSelection:
        componentAlertTitle = GlobalConstants.SelectDockableApplyWireFillErrorTitle;
        msgContent = GlobalConstants.SelectDockableApplyWireFillErrorBody;
        break;
      case PanelCircuitDataReturnCodes.WireFillApplied:
        const fittingCount = this.facade.getLocalStorage(GlobalConstants.DOCKABLE_PANEL_DATA_FITTING_COUNT);
        componentAlertTitle = GlobalConstants.SelectDockableApplyWireFillApplied;
        msgContent =
          GlobalConstants.SelectDockableApplyWireFillAppliedBody +
          ' ' +
          fittingCount +
          ' ' +
          GlobalConstants.SelectDockableApplyWireFillAppliedWithFittingCount;
        break;
      default:
        break;
    }
    this.facade.updateLocalStorage(GlobalConstants.MSG_TITLE, componentAlertTitle);
    this.facade.updateLocalStorage(GlobalConstants.MSG_CONTENT, msgContent);
  }

  public applyPanelData(): void {
    this.populateSelectedCircuits();
    this.selectedItem = {
      selectedProjectName: this.dockedState.projectAndPanelName[0],
      selectedPanelName: this.dockedState.projectAndPanelName[1],
      selectedCircuitLists: this.selectedCircuits,
      selectedSystemName: this.dockedState.selectedSystem,
      selectedCategory: this.dockedState.selectedCategory,
      selectedWireFillSpec: '',
    };
    this.facade.applyPanelDataLogic(this.selectedItem, false);
  }

  public populateSelectedCircuits(): void {
    this.selectedCircuits = [];
    this.dockedState = this.facade.getLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY);
    this.dockedState.checkBoxConfigs.forEach((element) => {
      this.selectedCircuits.push({ circuitName: element.label, isSelected: element.checked });
    });
  }

  public enableApplyBtn(newValue: boolean): void {
    if (newValue && this.checkedCircuit.length > 0) {
      this.disableApplyBtn = false;
    } else {
      this.disableApplyBtn = true;
    }
    this.cd.detectChanges();
  }

  /**
   * This method is used to update the fittings list
   */
  private async updateFittingsList(): Promise<void> {
    
    const fittingsList = await this.facade.getFittingsList(
      this.dockedState.selectedSystem, this.dockedState.selectedCategory, this.facade.getSysQueConnectOnState());

   fittingsList.forEach(fitting => {
      fitting.tooltipItems = this.facade.getTooltipItemInfo(fitting, this.selectedCategory);
    });

    this.dockedState.fittingList = fittingsList;
    this.facade.updateLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY, this.dockedState);
    this.cd.detectChanges();
  }

  //#region Copy-Paste logic

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    this.contextMenus?.forEach((contextMenu) => {
      if (contextMenu.visible) {
        contextMenu.visible = false;
      }
    });
  }

  public onRightClick(e: MouseEvent): void {
    if (this.selectedItems.length > 0 && (e.target as Element).tagName.toLowerCase() !== 'img') {
      this.showRightClickMenu = true;
      e.preventDefault();
    } else {
      this.showRightClickMenu = false;
    }
  }

  public onContextMenuShowing(event): void {
    this.contextMenus.forEach((contextMenu) => {
      if (contextMenu !== event.component) {
        contextMenu.visible = false;
      }
    });
  }

  public updateCopiedItemList(e: Item): void {
    this.selectedItems = [];
    this.copyPasteService.copiedItemsList$
      .pipe(
        filter((data) => data && data.length > 0),
        takeUntil(this.closed$)
      )
      .subscribe((data) => {
        this.selectedItems = data;
      });
    if (
      !this.selectedItems.some((item) => item.id === e.id && item.name === e.name && item.fittingType === e.fittingType)
    ) {
      this.selectedItems.push(e);
    }
    this.srcSelectedSystem = this.dockedState.selectedSystem;
    this.srcSelectedCategory = this.dockedState.selectedCategory;
    this.copyPasteService.setCopiedItemsList(this.selectedItems);
  }

  public onContextMenuItemClick(event): void {
    if (event.itemData.text === GlobalConstants.Paste) {
      this.warningMsg = '';
      this.copiedItemInfo = new PastedItemsInfo();
      this.destSelectedSystem = this.dockedState.selectedSystem;
      this.destSelectedCategory = this.dockedState.selectedCategory;
      if (this.validatePasteScenario() === false) {
        this.pasteError.emit(this.warningMsg);
      } else {
        this.checkForDuplicateItems();
      }
    }
  }

  public validatePasteScenario(): boolean {
    const ifDestCategoryIsCustomContent = this.checkIfCustomContent(this.destSelectedCategory);
    const ifSrcCategoryIsCustomContent = this.checkIfCustomContent(this.srcSelectedCategory);
    if (this.destSelectedSystem === this.srcSelectedSystem) {
      this.warningMsg = GlobalConstants.BothSelectedSystemSame;
      return false;
    }
    if (ifSrcCategoryIsCustomContent != 'true') {
      if (this.srcSelectedCategory !== this.destSelectedCategory) {
        this.warningMsg =
          GlobalConstants.FittingsBelogsTo +
          this.srcSelectedCategory +
          GlobalConstants.PleasePasteIn +
          this.srcSelectedCategory +
          "' tab.";
        return false;
      }
    } else {
      if (ifDestCategoryIsCustomContent != 'true') {
        this.warningMsg = GlobalConstants.CustomContentFitting;
        return false;
      }
    }

    this.getSelectedItemsDetails();
    const result = this.categories.filter((category) => {
      if (category.text == this.copiedItemInfo.category) {
        return true;
      }
    });
    if (result) {
      const isSrcClearance = this.dockedState.systemList.find((x) => x.name === this.srcSelectedSystem)?.isClearance;
      const isDestClearance = this.dockedState.systemList.find((x) => x.name === this.destSelectedSystem)?.isClearance;
      const response = isSrcClearance === isDestClearance;
      if (!response) {
        this.warningMsg = GlobalConstants.ClearanceSystemTypeMisMatch;
        return false;
      }
    }
    return true;
  }

  public checkIfCustomContent(categoryName: string): string {
    const selectedCategory = this.dockedState.categoryList.find((category) => category.tabName === categoryName);
    return selectedCategory ? selectedCategory.isCustomContent.toLowerCase() : '';
  }

  public checkForDuplicateItems(): void {
    this.facade
      .checkForDuplicateItems(this.copiedItemList)
      .pipe(takeUntil(this.closed$))
      .subscribe((response) => {
        let filteredList = response.filter((item) => item.isDuplicate);
        if (response.length > 0 && filteredList.length > 0) {
          this.onPasteEventLogic(response.filter((item) => !item.isDuplicate));
          this.warningMsg = "'" + this.dockedState.fittingList[0].name + GlobalConstants.DoReplaceExistingItem;
          this.showDuplicateError.emit(filteredList);
        } else {
          this.onPasteEventLogic(this.copiedItemList);
        }
      });
  }

  public async onPasteEventLogic(copiedItems: PastedItemsInfo[]): Promise<void> {
    await this.facade.executePasteItemsAction(copiedItems);
    this.facade.updateLocalStorage(GlobalConstants.CanExecutePasteLogic, false);
    this.updateStatus();
    this.resetItemStyle(null, false);

    // Mark for check to ensure the view is updated
    this.cd.markForCheck();
  }

  public updateStatus(): void {
    this.systemInfo$ = of(this.dockedState.systemList);
    this.categoryList$ = of(this.dockedState.categoryList);
    this.selectedItems = [];
    this.updateFittingsList();
    this.updateCopyPasteValues();
  }

  public resetItemStyle(item: Item, isCancel: boolean): void {
    this.categoryScope.resetStyle(item, isCancel);
  }

  public getSelectedItemsDetails(): void {
    this.copiedItemList = [];
    this.selectedItems.forEach((item) => {
      this.copiedItemInfo = new PastedItemsInfo();
      this.copiedItemInfo.copiedItem = item;
      this.copiedItemInfo.srcSystemName = this.srcSelectedSystem;
      this.copiedItemInfo.destSystemName = this.destSelectedSystem;
      this.copiedItemInfo.category = this.destSelectedCategory;
      this.copiedItemList.push(this.copiedItemInfo);
    });
  }

  public removeItem(item: Item): void {
    this.resetItemStyle(item, true);
    let srcCopied: Item[] = [];
    this.copyPasteService.copiedItemsList$.pipe(takeUntil(this.closed$)).subscribe((data) => {
      srcCopied = data;
    });
    const srcCopiedIndex = srcCopied.findIndex((i) => i.name === item.name);
    if (srcCopiedIndex !== -1) {
      srcCopied.splice(srcCopiedIndex, 1);
    }
    this.copyPasteService.setCopiedItemsList(srcCopied);
    this.facade.updateLocalStorage(GlobalConstants.DuplicateItemsUpdate, true);
  }

  //#endregion
}